<template>
  <div
    class="d-book-buy-button"
    :class="componentClass"
  >
    <a
      v-if="vm.firstStore"
      class="button"
      :href="vm.firstStore.url"
      rel="nofollow"
      target="_blank"
      title="Buy it now"
      @click="setupStore(vm.firstStore)"
    >
      <span class="big-copy">Buy it now</span>
      <span class="medium-copy">Buy now</span>
      <span class="small-copy">Buy</span>
    </a>

    <button
      v-if="(vm.storeLinks.length > 1)"
      class="toggle"
      type="button"
      @click="toggleOpen"
    >
      <DuiIconDown class="icon-white" />
    </button>

    <div class="content-container">
      <div class="content">
        <ul class="actions">
          <li
            v-for="storeLink in vm.storeLinks"
            :key="storeLink.store"
            class="action"
          >
            <a
              :href="storeLink.url"
              rel="nofollow"
              target="_blank"
              :title="('Buy on ' + storeLink.store)"
              @click.stop="setupStore(storeLink)"
            >
              <img
                class="store-icon"
                :src="storeLink.icon"
              >
              <span>Buy on <span>{{ storeLink.store }}</span></span>
            </a>
          </li>
        </ul>
      </div>
    </div>

    <DAmazonStoreSelection
      v-if="!vm.isAmazonActive && vm.isPickingAmazonStore"
      :book-slug="bookSlug"
      @amazon-store-picked="amazonStorePicked"
      @dismiss="dismissAmazonStoreSelection"
    />
  </div>
</template>

<script>
import contextMenuMixin from '@dui/mixins/context-menu';
import DAmazonStoreSelection from '@d-components/modals/d-amazon-store-selection/d-amazon-store-selection.vue';
import Window from '@d-shared/utils/window';

import AMAZON_IMG from '@d-assets/stores/amazon.png';
import APPLE_IMG from '@d-assets/stores/apple.png';
import BARNES_AND_NOBLE_IMG from '@d-assets/stores/barnes-and-noble.jpg';
import GOOGLE_PLAY_IMG from '@d-assets/stores/google-play.png';
import KOBO_IMG from '@d-assets/stores/kobo.jpg';
import WEBSITE_IMG from '@d-assets/icon-globe-dark.svg';

const STORE_ICONS = Object.freeze({
  amazon: AMAZON_IMG,
  appleBooks: APPLE_IMG,
  barnesAndNoble: BARNES_AND_NOBLE_IMG,
  googlePlay: GOOGLE_PLAY_IMG,
  kobo: KOBO_IMG,
  website: WEBSITE_IMG,
});

function _getStoreIcon(optionLabel) {
  switch (optionLabel) {
    case 'Amazon':
      return STORE_ICONS.amazon;
    case 'Apple Books':
      return STORE_ICONS.appleBooks;
    case 'Barnes & Noble':
      return STORE_ICONS.barnesAndNoble;
    case 'Google Play':
      return STORE_ICONS.googlePlay;
    case 'Kobo':
      return STORE_ICONS.kobo;
    case 'Website':
      return STORE_ICONS.website;
    default:
      return '';
  }
}

export default {
  components: {
    DAmazonStoreSelection,
  },
  mixins: [
    contextMenuMixin,
  ],
  props: {
    bookSlug: {
      type: String,
      required: true,
    },
    buyButtonClass: {
      type: String,
      default: null,
    },
    isAmazonStoreSet: {
      type: Boolean,
      default: false,
    },
    urlList: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    vm: {
      firstStore: null,
      isAmazonActive: true,
      isPickingAmazonStore: false,
      storeLinks: [],
    },
  }),
  computed: {
    amazonUrl() {
      return this.$store.getters['Book/get-amazon-url'];
    },
    componentClass() {
      return [
        this.vm.isOpen ? 'open' : '',
        (this.vm.storeLinks.length > 1) ? 'multiple-options' : '',
        this.buyButtonClass ? this.buyButtonClass : '',
      ].join(' ');
    },
  },
  watch: {
    'amazonUrl': function() {
      this.setAmazonActiveState();
    },
  },
  mounted() {
    this.$set(this.vm, 'isAmazonActive', this.isAmazonStoreSet);
    this.setupUrls();
    this.setAmazonActiveState();
  },
  methods: {
    amazonStorePicked(amazonBookUrl) {
      if (!amazonBookUrl || this.vm.isAmazonActive) {
        return;
      }

      const amazonUrl = this.vm.storeLinks.find(l => l.store === 'Amazon');
      if (!amazonUrl) {
        return;
      }

      this.$store.dispatch('Book/saveAmazonUrl', amazonBookUrl);

      amazonUrl.url = amazonBookUrl;
      this.$set(this.vm, 'isAmazonActive', true);

      Window.open(amazonBookUrl);
    },

    dismissAmazonStoreSelection() {
      this.$set(this.vm, 'isPickingAmazonStore', false);
    },

    setAmazonActiveState() {
      const amazonUrl = this.vm.storeLinks.find(l => l.store === 'Amazon');
      if (!amazonUrl) {
        return;
      }

      if (this.vm.isAmazonActive) {
        return;
      }

      amazonUrl.url = this.amazonUrl;
      this.$set(this.vm, 'isAmazonActive', !!(this.isAmazonStoreSet || this.amazonUrl));
    },

    setupStore(storeEntry) {
      if (storeEntry.store !== 'Amazon' || this.vm.isAmazonActive) {
        return;
      }

      this.showAmazonStoreSetup();
    },

    setupUrls() {
      const urls = [];
      this.urlList.forEach((urlEntry) => {
        if (!urlEntry.url) {
          return;
        }

        const adjustedUrl = {
          icon: _getStoreIcon(urlEntry.store),
          store: urlEntry.store,
          url: urlEntry.url,
        };

        urls.push(adjustedUrl);
      });

      this.$set(this.vm, 'storeLinks', urls);

      if (urls.length) {
        this.$set(this.vm, 'firstStore', urls[0]);
        return;
      }

      this.$set(this.vm, 'firstStore', null);
    },

    showAmazonStoreSetup() {
      this.$set(this.vm, 'isPickingAmazonStore', true);
    },
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";

.d-book-buy-button {
  display: inline-flex;

  @include makePopoverContainer();

  &.button-full {
    width: 100%;
  }

  &.button-full-sm {
    @include screenLessThan(md) {
      width: 100%;
    }
  }

  &.multiple-options {
    > a.button {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }

    button.toggle {
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }
  }

  button,
  .button {
    align-items: center;
    color: getColor($white);
    display: flex;
    justify-content: center;
  }

  a.button {
    background-color: getColor($yellow);
    color: getColor($white);
    flex: 1;

    &:hover {
      background-color: getColor($yellow, medium);
    }

    // iPhone 5S or earlier
    .medium-copy,
    .big-copy {
      display: none;
    }

    // SM screens
    @include screenMoreThanCustom(356px) {
      .medium-copy {
        display: block;
      }

      .small-copy,
      .big-copy {
        display: none;
      }
    }

    // Big screens
    @include screenMoreThanCustom(699px) {
      .big-copy {
        display: block;
      }

      .small-copy,
      .medium-copy {
        display: none;
      }
    }
  }

  button.toggle {
    background-color: getColor($yellow, medium);
    flex: 0;

    &:hover {
      background-color: getColor($yellow, dark);
    }
  }

  .content-container .action a {
    align-items: center;
    display: flex;

    span {
      flex: 1;
    }

    img {
      margin-right: $_2em;
      max-height: $_2em;
      max-width: $_2em;
    }
  }

  &.sm {
    button,
    .button {
      font-size: $font-size-sm;
      line-height: 1;
      padding: $_1em;
    }

    button.toggle {
      padding: $_1em;
    }
  }

  &.lg {
    button,
    .button {
      font-size: $font-size-lg;
      line-height: 0.9;
      padding: $_2em;
    }
  }
}
</style>
